<template>
  <div class="donut-classificacao" :style="donutStyle">
    <svg viewBox="0 0 160 160" class="donut-svg">
      <g v-if="total">
        <g
          v-if="!temMaisDeUmaClassificacao"
          class="donut-slice-group"
          @click="$emit('select', unicaClassificacao)"
          @mouseenter="hoverSlice = unicaClassificacao"
          @mouseleave="hoverSlice = null"
          @mousemove="atualizarMouse"
        >
          <circle
            class="donut-slice"
            cx="80"
            cy="80"
            r="48"
            :fill="unicaClassificacao.color"
          />

          <circle cx="80" cy="80" r="26" fill="#fff" />
        </g>

        <g v-else v-for="slice in slices" :key="slice.key">
          <path
            class="donut-slice"
            :d="slice.path"
            :fill="slice.color"
            stroke="#fff"
            stroke-width="2"
            @click="$emit('select', slice)"
            @mouseenter="hoverSlice = slice"
            @mouseleave="hoverSlice = null"
            @mousemove="atualizarMouse"
          />
        </g>

        <g v-for="slice in slices" :key="`label-${slice.key}`">
          <path class="donut-line" :d="slice.linePath" :stroke="slice.color" />

          <text
            class="donut-label"
            :x="slice.labelX"
            :y="slice.labelY"
            text-anchor="middle"
            dominant-baseline="middle"
          >
            {{ slice.value }}
          </text>
        </g>
      </g>

      <g
        v-if="hoverSlice"
        class="donut-tooltip"
        :transform="`translate(${mouse.x + 8}, ${mouse.y - 20})`"
      >
        <rect width="82" height="42" rx="6" />

        <text x="8" y="16" class="tooltip-title">
          {{ hoverSlice.label }}
        </text>

        <text x="8" y="31" class="tooltip-value">
          {{ hoverSlice.value }} equipe(s)
        </text>
      </g>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'DonutClassificacao',
  props: {
    item: {
      type: Object,
      required: true,
    },
    size: {
      type: Number,
      default: 150,
      validator: (value) => value >= 100,
    },
  },
  data() {
    return {
      hoverSlice: null,
      mouse: {
        x: 0,
        y: 0,
      },
    }
  },
  computed: {
    donutStyle() {
      return {
        '--donut-size': `${this.size}px`,
        '--donut-container-height': `${this.size}px`,
      }
    },

    valores() {
      return [
        {
          key: 'otima',
          label: 'Ótimo',
          value: Number(this.item.otima || 0),
          color: '#1212b6',
        },
        {
          key: 'boa',
          label: 'Bom',
          value: Number(this.item.boa || 0),
          color: '#38ba38',
        },
        {
          key: 'suficiente',
          label: 'Suficiente',
          value: Number(this.item.suficiente || 0),
          color: '#d5ba0a',
        },
        {
          key: 'regular',
          label: 'Regular',
          value: Number(this.item.regular || 0),
          color: '#c46500',
        },
      ].filter((item) => item.value > 0)
    },

    total() {
      return this.valores.reduce((total, item) => total + item.value, 0)
    },

    temMaisDeUmaClassificacao() {
      return this.valores.length > 1
    },

    unicaClassificacao() {
      return {
        ...this.valores[0],
        item: this.item,
      }
    },

    slices() {
      if (!this.total) return []

      let currentAngle = -90

      return this.valores.map((item) => {
        const angle = this.temMaisDeUmaClassificacao
          ? (item.value / this.total) * 360
          : 359.99

        const startAngle = currentAngle
        const endAngle = currentAngle + angle

        currentAngle = endAngle

        return {
          ...item,
          item: this.item,
          path: this.describeArc(80, 80, 48, 26, startAngle, endAngle),
          linePath: this.getLinePath(80, 80, 52, 60, startAngle, endAngle),
          ...this.getLabelPosition(80, 80, 68, startAngle, endAngle),
        }
      })
    },
  },

  methods: {
    /**
     * Converte uma posição polar em coordenadas cartesianas para uso no SVG.
     *
     * @param {Number} cx Posição X central.
     * @param {Number} cy Posição Y central.
     * @param {Number} radius Raio usado no cálculo.
     * @param {Number} angleInDegrees Ângulo em graus.
     * @return {Object}
     */
    polarToCartesian(cx, cy, radius, angleInDegrees) {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180

      return {
        x: cx + radius * Math.cos(angleInRadians),
        y: cy + radius * Math.sin(angleInRadians),
      }
    },

    /**
     * Monta o caminho SVG de uma fatia do donut.
     *
     * @param {Number} cx Posição X central.
     * @param {Number} cy Posição Y central.
     * @param {Number} outerRadius Raio externo.
     * @param {Number} innerRadius Raio interno.
     * @param {Number} startAngle Ângulo inicial da fatia.
     * @param {Number} endAngle Ângulo final da fatia.
     * @return {String}
     */
    describeArc(cx, cy, outerRadius, innerRadius, startAngle, endAngle) {
      const startOuter = this.polarToCartesian(cx, cy, outerRadius, endAngle)
      const endOuter = this.polarToCartesian(cx, cy, outerRadius, startAngle)
      const startInner = this.polarToCartesian(cx, cy, innerRadius, startAngle)
      const endInner = this.polarToCartesian(cx, cy, innerRadius, endAngle)

      const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1

      return [
        `M ${startOuter.x} ${startOuter.y}`,
        `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 0 ${endOuter.x} ${endOuter.y}`,
        `L ${startInner.x} ${startInner.y}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${endInner.x} ${endInner.y}`,
        'Z',
      ].join(' ')
    },

    /**
     * Retorna o caminho curvado da linha guia entre a fatia e o label externo.
     *
     * @param {Number} cx Posição X central.
     * @param {Number} cy Posição Y central.
     * @param {Number} radius1 Raio inicial da linha.
     * @param {Number} radius2 Raio final da linha.
     * @param {Number} startAngle Ângulo inicial da fatia.
     * @param {Number} endAngle Ângulo final da fatia.
     * @return {String}
     */
    getLinePath(cx, cy, radius1, radius2, startAngle, endAngle) {
      const midAngle = startAngle + (endAngle - startAngle) / 2

      const p1 = this.polarToCartesian(cx, cy, radius1, midAngle)
      const p2 = this.polarToCartesian(cx, cy, radius2, midAngle)

      const control = this.polarToCartesian(
        cx,
        cy,
        (radius1 + radius2) / 2,
        midAngle + 4
      )

      return `M ${p1.x} ${p1.y} Q ${control.x} ${control.y} ${p2.x} ${p2.y}`
    },

    /**
     * Retorna a posição externa do label da fatia.
     *
     * @param {Number} cx Posição X central.
     * @param {Number} cy Posição Y central.
     * @param {Number} radius Raio usado para posicionar o label.
     * @param {Number} startAngle Ângulo inicial da fatia.
     * @param {Number} endAngle Ângulo final da fatia.
     * @return {Object}
     */
    getLabelPosition(cx, cy, radius, startAngle, endAngle) {
      const midAngle = startAngle + (endAngle - startAngle) / 2
      const point = this.polarToCartesian(cx, cy, radius, midAngle)

      return {
        labelX: point.x,
        labelY: point.y,
      }
    },

    /**
     * Atualiza a posição do mouse dentro do SVG.
     *
     * @param {MouseEvent} event Evento do mouse.
     * @return {void}
     */
    atualizarMouse(event) {
      const svg = event.currentTarget.ownerSVGElement || event.currentTarget
      const rect = svg.getBoundingClientRect()

      this.mouse = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.donut-classificacao {
  width: 100%;
  height: var(--donut-container-height, 150px);
  display: flex;
  align-items: center;
  justify-content: center;

  .donut-svg {
    width: var(--donut-size, 150px);
    height: var(--donut-size, 150px);
    overflow: visible;
  }

  .donut-slice,
  .donut-slice-group {
    cursor: pointer;
    transition: transform 0.25s ease, filter 0.25s ease;
    transform-origin: center;

    &:hover {
      transform: scale(1.06);
      filter: brightness(1.08);
    }
  }

  .donut-line {
    fill: none;
    stroke-width: 1;
    stroke-linecap: round;
    opacity: 0.8;
  }

  .donut-label {
    font-size: 12px;
    font-weight: 900;
    fill: #475569;
    pointer-events: none;
  }

  .donut-tooltip {
    pointer-events: none;

    rect {
      fill: #fff;
      stroke: #dbe3ec;
      stroke-width: 1;
      filter: drop-shadow(0 4px 10px rgba(15, 23, 42, 0.18));
    }

    .tooltip-title {
      font-size: 11px;
      font-weight: 800;
      fill: #1f2937;
    }

    .tooltip-value {
      font-size: 10px;
      fill: #475569;
    }
  }
}
</style>
